<template>
  <v-sheet class="pa-5">
    <div v-if="event">
        <v-row>
          <v-col v-if="event.status === 'cancelled'">
            <v-alert type="warning" :colored-border="true" border="left">
              This event is {{event.status}} <v-btn class="ml-3" @click="restoreEvent">Restore</v-btn>
            </v-alert>
          </v-col>
          <v-col cols="12">
            <div class="d-flex justify-space-between">
            <v-btn v-if="event && allowDelete && event.status === 'confirmed'" @click="deleteEvent">
              <v-icon class="mr-2">mdi-delete-outline</v-icon>
              Delete Event on Google Calendar</v-btn>

            <v-chip outlined :href="event.htmlLink" target="_blank">
                <v-icon class="mr-2">mdi-calendar</v-icon>View in Google
                Calendar
              </v-chip>

              <!-- <v-btn @click="sendNotifications">
                Send Notifications
              </v-btn> -->
            </div>
          </v-col>
          <v-col cols="12" sm="6" lg="4">
            <div class="d-flex justify-space-between">
              <div class="text-h6">
                {{ event.summary }}
              </div>
            </div>
            <div
              class="mt-5"
              v-if="isRecurring(event)"
            >
              <div class="text-subtitle-2">Recurring schedule</div>
              <div v-html="rRuleToText(event)"></div>
            </div>
            <div v-else>
              {{ formatDate(event.start, "date time") }} -
              {{ formatDate(event.end, "time") }}
            </div>

            <div class="mt-3" v-html="event.description"></div>

            <!-- Reminders set for this event -->
            <div v-if="event.reminders && event.reminders.overrides" class="mt-5">
              <div class="text-subtitle-2">Scheduled Reminders:</div>
              <ul class="mt-3">
                <li
                  v-for="reminder in event.reminders.overrides"
                  :key="reminder.minutes"
                >
                  {{ reminder.method }}
                  {{ formatMinutes(reminder.minutes) }} prior
                </li>
              </ul>
            </div>
            <div class="mt-5" v-else-if="allowSetReminders">
              <v-alert colored-border
                color="primary" border="left" type="warning">No reminders set</v-alert>
              <div class="mt-5">
                <v-select
                  v-model="selectedReminders"
                  dense
                  multiple
                  clearable
                  :items="reminders"
                  label="Schedule Reminders"
                  item-value="value"
                ></v-select>
                <v-btn @click="updateReminders">Update Reminders</v-btn>
              </div>
            </div>
          </v-col>

          <v-col v-if="event.conferenceData" cols="12" sm="6" lg="4">
            <div class="d-flex justify-space-between">
              <div class="text-h6">Conference Info</div>
            </div>
            <div class="mt-5">
              <div class="d-flex flex-column flex-lg-row">
                Meeing Code: 
                <v-text-field class="ml-2"  v-if="editConference" label="Conference Id" v-model="event.conferenceData.conferenceId"/>
                <span class="ml-2" v-else>{{ event.conferenceData.conferenceId }}</span>
                <!-- <v-btn  v-if="editConference"  class="ml-2" small @click="updateConferenceId">Update</v-btn>
                <v-btn v-else @click="editConference = true" class="ml-2" small icon>
                  <v-icon>mdi-pencil</v-icon>
                </v-btn> -->
              </div>
            </div>
            <div
              class="my-5"
              v-for="entry in event.conferenceData.entryPoints"
              :key="entry.uri"
            >
              <template v-if="entry.entryPointType != 'more'">
                <span class="group">
                  <v-icon>{{
                    entry.entryPointType == "video" ? "mdi-video" : "mdi-phone"
                  }}</v-icon>
                  {{ entry.entryPointType }}
                </span>
                <br />
                <div class="mt-3">
                  <a :href="entry.uri">{{ entry.label }}</a>
                  <br />
                  <template v-if="entry.pin">
                    Pin: {{ entry.pin }}
                    <br />
                  </template>
                </div>
              </template>
            </div>
          </v-col>

          <v-col cols="12" sm="12" lg="4">
            <div>
              <div class="text-h6">Attendees</div>
            </div>
            <v-list v-if="event.attendees">
              <v-list-item
                class="ml-0 pl-0" 
                v-for="attendee in event.attendees"
                :key="attendee ? attendee.id:0"
              >
                <v-list-item-content>
                  <template v-if="attendee.name"
                    >{{ attendee.name }} -</template
                  >
                  {{ attendee.email }}
                  <span v-if="attendee.responseStatus"
                    >({{ attendee.responseStatus }})</span
                  >
                </v-list-item-content>
                <v-list-item-action v-if="allowRemoveAttendee">
                  <v-btn @click="removeAttendee(attendee.email)" icon>
                    <v-icon> mdi-delete-outline </v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <div class="mt-5" v-else>
              <v-alert colored-border
                color="secondary" border="left" type="info">No attendee found</v-alert>
            </div>
            <div v-if="allowAddAttendee" class="d-flex flex-lg-row flex-column justify-space-between">
              <div style="width: 100%">
                <v-text-field hide-details v-model="newAttendee" label="Add new attendee" />
                <v-checkbox v-model="sendNotification" :value="true" label="Send Notification"/>
              </div>
              <v-btn @click="addAttendee" class="ml-3 mt-3"> Add </v-btn>
            </div>
          </v-col>
        </v-row>

        <div class="text-caption mt-5" v-if="event && event.id">
          Event ID: {{event.id}}
        </div>
    </div>
    <div v-else>
      <v-skeleton-loader type="card-heading, image" />
    </div>
  </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { rrulestr } from "rrule";
import moment from "moment";

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: true
    },
    allowAddAttendee: {
      type: Boolean,
      required: false,
      default: true
    },
    allowRemoveAttendee: {
      type: Boolean,
      required: false,
      default: true
    },
    allowSetReminders: {
      type: Boolean,
      required: false,
      default: true
    },
    owner: {
      type: String,
      required: false,
    }
  },
  data: () => {
    return {
      event: null,
      sendNotification: false,
      editConference: false,
      newAttendee: '',
      selectedReminders: [],
      reminders: [{text: '3 Days', value: 4320}, {text: '1 Day', value: 1440}, {text: '5 hours', value: 300}, {text: '1 hour', value: 60}],
    };
  },
  mounted() {
    this.event = this.value;
  },
  computed:{
      ...mapGetters(["isAppLoading"]),
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage"]),
    rRuleToText(event) {
      if(!event.start.dateTime){
        return 'Unknown event time'
      }
      const startDate = this.formatDate(event.start, "date with dow");
      const startTime = this.formatDate(event.start, "time");
      const endTime = this.formatDate(event.end, "time");
      if (this.isRecurring(event)) {
        const recurrenceRrule = event.recurrence.find(r => r.startsWith('RRULE:'));
        const recurrenceExdate = event.recurrence.find(r => r.startsWith('EXDATE;'));
        const rrule = recurrenceRrule + (recurrenceExdate ? `\n${recurrenceExdate.replace(/EXDATE;.*:/, 'EXDATE:')}`:'');

        const ruleSet = rrulestr(rrule, {forceset: true});
        const ruleText = ruleSet.rrules().map(r => r.toText()).join('\n');
        const exDates = ruleSet.exdates()
        let excludes = '';
        if(exDates && exDates.length > 0){
          excludes = `excluding ${exDates.map(d => moment(d).format("MMM DD")).join(', ')}`;
        }

        return `Starts ${startDate} from ${startTime} - ${endTime},<br/> ${ruleText} <br/> ${excludes}`;
      } else {
        return `${startDate} ${startTime}-${endTime}`;
      }
    },
    isRecurring(event){
      if(event && event.recurrence && event.recurrence.length > 0){
        return true;
      }
      else{
        return false;
      }
    },
    formatDate(d, mode) {
      if(!d.dateTime){
        console.log(d);
        return '';
      }

      if (mode === "time") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("h:mm a");
      } else if (mode === "date time") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("MMM DD, YYYY h:mm a");
      } else if (mode === "date time with dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("ddd MMM DD, YYYY h:mm a");
      } else if (mode === "date") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("MMM DD, YYYY");
      } else if (mode === "date with dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("ddd MMM DD, YYYY");
      } else if (mode === "dow") {
        return new moment.utc(d.dateTime.value).utcOffset(d.dateTime.timeZoneShift).format("dddd");
      }
    },
    formatMinutes(mins) {
      const days = Math.floor(mins / (60 * 24));
      const hours = Math.floor(mins / 60);
      const minutes = mins % 60;
      const text =
        (days > 0 ? days + " days" : "") +
        (hours % 24 > 0 ? (hours % 24) + " hours" : "") +
        (minutes > 0 ? minutes + " minutes" : "");
      return text;
    },
    deleteEvent() {
      if (confirm("Are you sure you want to delete this event?")) {
        const data = {
          event_id: this.event.id,
          calendar_account: this.event.organizer.email,
        };
        this.$axios
          .post("/google-calendar/delete-event", data)
          .then(() => {
            this.addSuccessMessage("Event deleted");
            console.log('emitting delete event')
          });
          this.$emit("deletedEvent", this.event);
          
      }
    },
    restoreEvent() {
      const data = {
        event_id: this.event.id,
        calendar_account: this.event.organizer ? this.event.organizer.email:this.owner
      };
      this.$axios
        .post("/google-calendar/restore-event", data)
        .then((resp) => {
          this.event = resp.data.data;
          this.addSuccessMessage("Event restored");
          this.$emit("input", this.event);
        })
        .catch( (error) =>{
          this.addErrorMessage(error.data.message || 'Error restoring event')
        })
    },
    removeAttendee(email) {
      const params = {
        event_id: this.event.id,
        calendar_account: this.event.organizer.email,
        email: email,
      };
      this.$axios
        .post("/google-calendar/remove-attendee", null, { params })
        .then((resp) => {
          console.log("remaining attendees on this event");
          console.log(resp.data.data);
          console.log(`removing ${email} from index ${this.event.attendees.findIndex(a => a.email == email)}`)
          this.event.attendees.splice(this.event.attendees.findIndex(a => a.email == email), 1);
          console.log(this.event.attendees)
          this.addSuccessMessage("Attendee removed");
        });
    },
    addAttendee() {

      const data = {
        event_id: this.event.id,
        calendar_account: this.event.organizer.email,
        send_notification: this.sendNotification,
        emails: [this.newAttendee],
      };
      this.$axios
        .post("/google-calendar/add-attendee", data)
        .then((resp) => {
          const attendees = resp.data.data;
          const attendee = attendees.find((a) => a.email == this.newAttendee);
          this.newAttendee = "";
          if(this.event.attendees && this.event.attendees.length > 0){
            this.event.attendees.push(attendee);
          }
          else{
            this.event.attendees = [attendee];
          }
          this.addSuccessMessage("Attendee added");
        })
        .catch((error) => {
          this.addErrorMessage(error.data.message || 'Error adding attendee');
        })
    },
    updateReminders(){
      const data = {
        event_id: this.event.id,
        calendar_account: this.event.organizer.email,
        reminders: this.selectedReminders,
      };

      this.$axios.post('/google-calendar/update-reminder', data)
        .then( (resp) => {
          this.addSuccessMessage('Calendar event reminders updated')
          const updatedEvent = resp.data.data;
          this.event.reminders = updatedEvent.reminders;
        })
    },
    sendNotifications(){
      const data = {
        event_id: this.event.id,
        calendar_account: this.event.organizer.email,
      };

      this.$axios.post('/google-calendar/send-notifications', data)
        .then( (resp) => {
          this.addSuccessMessage('Notifications sent to guests')
          console.log(resp)
        })
    },
    updateConferenceId(){
      const data = {
        event_id: this.event.id,
        calendar_account: this.event.organizer.email,
        new_conference_id: this.event.conferenceData.conferenceId
      };

      this.$axios.post('/google-calendar/update-conference-id', data)
        .then( () => {
          this.addSuccessMessage('Conference ID updated')
          this.editConference = false;
        })
        .catch( (error) => {
          this.addErrorMessage(error.data.message || 'Error updating conference ID');
        })
    }
  },
};
</script>

<style>
</style>